import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceBoard } from "@/lib/prices.functions";
import {
  PRICE_SOURCES,
  SEALED_CATEGORY_LABEL,
  buildSealedRows,
  buildPriceRows,
  formatMoney,
  SEALED_PRODUCTS,
  type SealedCategory,
} from "@/lib/prices-shared";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { RatingStars } from "@/components/CardRating";
import { sealedImage } from "@/lib/sealed-images";

import { AffiliateDisclosure, formatUtcStamp } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, toolSchema, productGraphSchema } from "@/lib/schema";

const URL = "https://palstars.com/marketplace";
const TITLE = "Palworld TCG Marketplace: Buy & Sell Bulk Cards";
const DESCRIPTION =
  "The definitive marketplace platform for Palworld TCG bulk orders. Check buying signals, secure sealed product inventory, and list card singles.";

const CATEGORIES: SealedCategory[] = ["pack", "box", "deck", "case", "merch"];

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld TCG marketplace page?",
    a: "It is a live buying board for sealed Palworld OFFICIAL CARD GAME product and official merchandise. Every row shows the MSRP anchor, today's validated eBay asking price, how many listings were sampled and direct marketplace links.",
  },
  {
    q: "Where does the marketplace price data come from?",
    a: "Prices are drawn from validated marketplace feeds across the eBay US and eBay UK sites, converted to USD. Listing titles are validated first, so bulk lots, empty wrappers, proxies and mismatched products never enter a product's median.",
  },
  {
    q: "Is a Palworld booster box cheaper than buying single packs?",
    a: "Almost always yes. A booster box holds 12 packs, so divide the box asking price by 12 and compare it with the single-pack row on this page. Boxes only lose that edge when a set sells out and box prices spike above 12 times the pack price.",
  },
  {
    q: "Does Pal Stars earn money from these links?",
    a: "Yes, and it is disclosed on the page. Pal Stars participates in the eBay Partner Network, so clicking a marketplace link and buying can earn this site a commission. Commissions never change the price you pay and never change how a product is priced or ordered here.",
  },
  {
    q: "How often do marketplace prices update?",
    a: "The sealed and merchandise feed re-syncs every day at 07:10 UTC, and the timestamp of the data you are looking at is printed in the affiliate disclosure block on this page.",
  },
  {
    q: "Where is the cheapest place to buy Palworld TCG cards and sealed product?",
    a: "There is no single cheapest storefront. Compare the eBay US and eBay UK rows on this page against MSRP, then open the same product on the marketplaces listed on the Palworld TCG price chart. Factor in shipping and import duty before deciding, because a lower headline price from another region often loses to a domestic listing once postage is added.",
  },
  {
    q: "How do I avoid fake or mislabelled Palworld listings?",
    a: "Check the printed English card number in the listing photos with the Pal Stars card checker, confirm the set, colour, cost and rarity match, and be suspicious of sealed product priced far under MSRP or described as a proxy, custom, reprint or bundle. Pal Stars filters those title patterns out of its medians for the same reason.",
  },
  {
    q: "Can I track what I bought on the marketplace?",
    a: "Yes. Add any card you buy to your Palworld card collection binder at /collection with the price you paid, then log the price you sold or list it for. The binder shows profit and ROI per card and can be shared with a public link that needs no account to open.",
  },
  {
    q: "Should I buy singles or open sealed product?",
    a: "If you want a specific card, buying the single is almost always cheaper than opening packs for it — run the numbers on the Palworld pack calculator, which turns today's pack price and the pull rate into a cost per chase card. Sealed product only wins when you want the opening experience or the sealed premium itself.",
  },
];

export const Route = createFileRoute("/marketplace")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG Marketplace — sealed & merch prices" },
      { property: "og:description", content: "Live eBay asking and sold prices for Palworld packs, boxes, Trial Decks, cases and official merch." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Price Chart", "/prices"], ["Marketplace", "/marketplace"]]),
      toolSchema({ name: "Palworld TCG Marketplace", description: DESCRIPTION, path: "/marketplace" }),
      faqSchema(FAQ, URL),
      // Every row on this board renders the product name, its MSRP anchor and
      // marketplace deep links, so each is marked up as a Product with the same
      // MSRP as the visible anchor price. Emitted as standalone products (not a
      // carousel) because each one is described here rather than on its own page.
      productGraphSchema(
        SEALED_PRODUCTS.map((p) => {
          const img = sealedImage(p.key);
          return {
            name: p.name,
            description: `${p.name} — ${p.detail}. Official Palworld ${SEALED_CATEGORY_LABEL[p.category].toLowerCase()} tracked against MSRP with live eBay asking prices.`,
            path: `/marketplace#p-${p.key}`,
            image: img.src.startsWith("http") ? img.src : `https://palstars.com${img.src}`,
            sku: p.key,
            category: SEALED_CATEGORY_LABEL[p.category],
            lowPrice: p.msrp,
            offerUrl: PRICE_SOURCES.find((source) => source.id === "ebay")?.sealed?.(p.query),
          };
        }),
      ),
    ],
  }),
  component: MarketplacePage,
});

function MarketplacePage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const rows = useMemo(() => buildSealedRows(data?.sealed ?? []), [data?.sealed]);
  const [cat, setCat] = useState<SealedCategory | "all">("all");
  const shown = cat === "all" ? rows : rows.filter((r) => r.category === cat);

  const packRow = rows.find((r) => r.key === "bp01-pack");
  const boxRow = rows.find((r) => r.key === "bp01-box");
  const perPack = boxRow ? (boxRow.market ?? boxRow.msrp) / 12 : null;
  const singlePack = packRow ? (packRow.market ?? packRow.msrp) : null;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · marketplace</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld TCG Marketplace</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Live buying prices for sealed Palworld OFFICIAL CARD GAME product and official Palworld merchandise. Each row
          carries the MSRP anchor, today's validated asking price, the number of listings sampled and direct marketplace
          links. Prices were updated as of{" "}
          {formatUtcStamp(data?.updated)}.
        </p>
      </header>

      <AffiliateDisclosure updated={data?.updated} />

      <p className="text-sm">
        <Link
          to="/guides/$slug"
          params={{ slug: "palworld-tcg-sleeve-card-set-vol-1" }}
          className="font-semibold text-primary hover:underline"
        >
          Palworld Sleeve &amp; Card Set Vol.1: cards, parallels and product details →
        </Link>
      </p>

      {singlePack !== null && perPack !== null && (
        <section className="glass rounded-2xl p-5">
          <h2 className="text-xl font-bold">Box or single packs?</h2>
          <p className="aeo-answer mt-2 text-sm text-muted-foreground">
            A Dawn of Palpagos booster box works out at{" "}
            <strong className="text-foreground tabular-nums">{formatMoney(perPack, "USD")}</strong> per pack against{" "}
            <strong className="text-foreground tabular-nums">{formatMoney(singlePack, "USD")}</strong> for a single pack —{" "}
            {perPack <= singlePack
              ? `the box is the cheaper way to open, saving about ${formatMoney(singlePack - perPack, "USD")} per pack.`
              : `single packs are currently cheaper per pack, so the box is only worth it for the sealed premium.`}{" "}
            Run the numbers on your own budget with the{" "}
            <Link to="/pack-calculator" className="font-semibold text-primary hover:underline">
              Palworld pack calculator
            </Link>
            .
          </p>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        {(["all", ...CATEGORIES] as const).map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            aria-pressed={cat === c}
            className={`rounded-lg border px-3 py-1.5 text-sm ${cat === c ? "border-primary bg-primary/10 text-foreground" : "border-border/60 text-muted-foreground hover:border-primary/60"}`}
          >
            {c === "all" ? "Everything" : SEALED_CATEGORY_LABEL[c]}
          </button>
        ))}
      </div>

      <section>
        <h2 className="text-2xl font-bold">Palworld sealed product & merchandise listings</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p) => {
            const img = sealedImage(p.key);
            return (
            <article key={p.key} id={`p-${p.key}`} className="glass overflow-hidden rounded-2xl">
              <div className="relative aspect-[4/3] w-full overflow-hidden border-b border-border/60 bg-background/40">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  width={768}
                  height={576}
                  className="h-full w-full object-contain p-3 transition-transform duration-300 hover:scale-[1.04]"
                />
                <span className="absolute left-2 top-2 rounded-md bg-background/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
                  {SEALED_CATEGORY_LABEL[p.category]}
                </span>
                {!img.official && (
                  <span className="absolute right-2 top-2 rounded-md bg-background/80 px-2 py-0.5 text-[10px] text-muted-foreground">
                    Category photo
                  </span>
                )}
              </div>
              <div className="p-4">
              <h3 className="font-bold">{p.name}</h3>
              {p.key === "merch-sleeve-set" ? (
                <Link
                  to="/guides/$slug"
                  params={{ slug: "palworld-tcg-sleeve-card-set-vol-1" }}
                  className="mt-1 inline-block text-xs font-semibold text-primary hover:underline"
                >
                  View all five SS01 cards and parallel foils →
                </Link>
              ) : null}
              <RatingStars code={`P-${p.key}`} label={p.name} className="mt-1" />
              <p className="text-xs text-muted-foreground">{p.detail}</p>
              <p className="mt-2 text-lg font-bold tabular-nums">
                {formatMoney(p.market ?? p.msrp, "USD")}{" "}
                <span className="text-xs font-normal text-muted-foreground">{p.market === null ? "MSRP" : "asking"}</span>
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {p.market !== null ? `MSRP ${formatMoney(p.msrp, "USD")}` : ""}
                {p.listings ? ` · ${p.listings} listings` : ""}
                {p.day ? ` · ${p.day}` : ""}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {PRICE_SOURCES.filter((s) => s.sealed).map((s) => (
                  <a
                    key={s.id}
                    href={s.sealed!(p.query)}
                    onClick={() =>
                      trackAffiliateClick({
                        source: s.id,
                        kind: p.category === "merch" ? "merch" : "sealed",
                        targetKey: p.key,
                      })
                    }
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="rounded-md border border-border/60 px-2 py-1 text-[11px] hover:border-primary/60"
                    title={s.note}
                  >
                    {s.label}
                  </a>
                ))}
              </div>
              </div>
            </article>
            );
          })}

        </div>
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Keep going</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> every printed single.</li>
          <li><Link to="/pack-calculator" className="font-semibold text-primary hover:underline">Palworld pack calculator →</Link> cost per chase card.</li>
          <li><Link to="/merch" className="font-semibold text-primary hover:underline">Palworld merchandise hub →</Link></li>
          <li><Link to="/cards" className="font-semibold text-primary hover:underline">Palworld card database →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld marketplace FAQ" />
    </div>
  );
}
